// Profile.js
// This component handles the profile page, including navigation between sections like update profile, invoices, and onboarding questions.
// It uses React Router for navigation and Redux for state management.

import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import "./Profile.css";
import ProfileForm from "./ProfileForm";
import InvoiceSection from "./InvoiceSection";
import UpdateOnboardingSection from "./UpdateOnboardingSection";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import QuizIcon from "@mui/icons-material/Quiz";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getSubscriptionDetails } from "../../../services/clientWebsite";
import { useDispatch, useSelector } from "react-redux";
import { updatePurchasedSubscription } from "../../../redux/slices/authSlice";

// Define sidebar options for navigation tabs
const sidebarOptions = [
  { key: "update", label: "Update Profile", icon: <PersonIcon /> },
  { key: "invoice", label: "Invoice", icon: <ReceiptIcon /> },
  { key: "onboarding", label: "Onboarding Questions", icon: <QuizIcon /> },
];

const Profile = () => {
  const { section } = useParams(); // Get the section name from URL parameters
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeSection, setActiveSection] = useState("update");

  const location = useLocation();

  // Effect to handle section changes based on URL or query params
  useEffect(() => {
    // Check query param session_id for redirecting to invoice section if present
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get("session_id");

    if (
      section === "invoice" ||
      section === "update" ||
      section === "onboarding"
    ) {
      setActiveSection(section);
    } else {
      if (sessionId) {
        setActiveSection("invoice");
      }
    }
  }, [section, location.search]);

  // Function to fetch user's subscription details from the API
  const handleFetchUserSubscription = async () => {
    try {
      const response = await getSubscriptionDetails();
      if (response.success) {
        const subscriptionId =
          response.data[0]?.subscription_details?.subscription_id;
        const chat_enable = response.data[0]?.user_subscription?.chat_enable;

        console.log("Chat Enabled:", subscriptionId, chat_enable);

        // Dispatch action to update subscription details in Redux store
        dispatch(
          updatePurchasedSubscription({
            purchasedSubscription: subscriptionId,
            chatEnabled: chat_enable,
            activeSubscription: response.data[0], // Pass full subscription data for blueprint detection
          })
        );
      }
    } catch (error) {
      console.error("error", error);
    }
  };

  // Effect to fetch subscription details on component mount
  useEffect(() => {
    handleFetchUserSubscription();
  }, []);

  return (
    <div className="profile-page">
      {/* Header section for the profile page */}
      <div className="profile-header">
        <div className="profile-container">
          <Typography
            className="Profile-heading"
            sx={{ textTransform: "uppercase" }}
          >
            Profile
          </Typography>
        </div>
      </div>

      {/* Main content area */}
      <div className="profile-container">
        {/* Navigation sidebar */}
        <div className="profile-nav">
          <ul className="nav-tabs">
            {sidebarOptions.map((option) => (
              <li key={option.key}>
                <button
                  className={`nav-tab ${
                    activeSection === option.key ? "active" : ""
                  }`}
                  onClick={() => navigate(`/profile/${option.key}`)}
                  aria-selected={activeSection === option.key}
                >
                  <span className="nav-tab-icon">{option.icon}</span>
                  <span className="nav-tab-label">{option.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Dynamic content based on active section */}
        <div className="profile-content">
          {activeSection === "update" && <ProfileForm />}
          {activeSection === "invoice" && <InvoiceSection />}
          {activeSection === "onboarding" && <UpdateOnboardingSection />}
        </div>
      </div>
    </div>
  );
};

export default Profile;
